/**
 * Locale-aware formatting.
 *
 * Every string a listener reads that encodes a date, a duration or a count goes through
 * here rather than through hand-rolled arithmetic and English literals. `Intl.*` is part of
 * the platform, so this costs no bundle weight and gets us every locale the browser knows —
 * including the RTL ones the stylesheet's logical properties are there to support.
 *
 * Formatters are cached: constructing one is the expensive part, and a list view formats a
 * date per row.
 */

const DAY_MS = 86_400_000;

/** The user's locale, resolved once. `undefined` lets `Intl` pick the browser default. */
function locale(): string | undefined {
  return typeof navigator === 'undefined' ? undefined : navigator.language || undefined;
}

const relativeCache = new Map<string, Intl.RelativeTimeFormat>();
const dateCache = new Map<string, Intl.DateTimeFormat>();

function relativeFormatter(): Intl.RelativeTimeFormat {
  const key = locale() ?? '';
  let formatter = relativeCache.get(key);
  if (!formatter) {
    formatter = new Intl.RelativeTimeFormat(locale(), { numeric: 'auto' });
    relativeCache.set(key, formatter);
  }
  return formatter;
}

function dateFormatter(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale() ?? ''}|${JSON.stringify(options)}`;
  let formatter = dateCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale(), options);
    dateCache.set(key, formatter);
  }
  return formatter;
}

/** Sentence-case the first character, which `Intl` gives us lowercased ("yesterday"). */
function upperFirst(value: string): string {
  return value.charAt(0).toLocaleUpperCase(locale()) + value.slice(1);
}

/**
 * A publication date as a listener scans it: "Today", "Yesterday", "3 days ago", then an
 * absolute date once the relative form stops being useful.
 *
 * Day boundaries are counted in whole local days rather than in elapsed milliseconds, so an
 * episode published at 23:50 reads as "Yesterday" at 00:10 rather than "Today".
 */
export function formatDate(ms: number, now: number = Date.now()): string {
  const days = calendarDaysBetween(ms, now);

  if (days >= 0 && days < 7) return upperFirst(relativeFormatter().format(-days, 'day'));

  const sameYear = new Date(ms).getFullYear() === new Date(now).getFullYear();
  return dateFormatter({
    day: 'numeric',
    month: 'short',
    year: sameYear ? undefined : 'numeric',
  }).format(ms);
}

/** Whole calendar days from `ms` to `now`, in the viewer's own time zone. */
function calendarDaysBetween(ms: number, now: number): number {
  const startOfDay = (value: number) => {
    const d = new Date(value);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  };
  return Math.round((startOfDay(now) - startOfDay(ms)) / DAY_MS);
}

/**
 * A duration spoken rather than shown: "1 hour 5 minutes".
 *
 * `formatTime` in the player state renders the clock face a sighted listener reads off the
 * scrubber; a screen reader announcing "one two colon zero five" is not that, so the
 * accessible name of a duration uses this instead (see `EpisodeRow`).
 */
export function formatDurationWords(totalSec: number | null | undefined): string {
  if (!totalSec || !Number.isFinite(totalSec) || totalSec <= 0) return 'Unknown length';
  const total = Math.round(totalSec);
  // A sub-half-second duration rounds to zero, and "0 seconds" is a worse answer than
  // admitting the feed did not say.
  if (total === 0) return 'Unknown length';
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  const parts: string[] = [];
  if (hours) parts.push(plural(hours, 'hour'));
  if (minutes) parts.push(plural(minutes, 'minute'));
  // Seconds only matter when they are all there is; nobody needs "1 hour 5 minutes 3 seconds".
  if (!hours && !minutes) parts.push(plural(seconds, 'second'));
  return parts.join(' ');
}

function plural(count: number, unit: string): string {
  return `${count} ${unit}${count === 1 ? '' : 's'}`;
}
